import { useCallback } from 'react';
import { usePinnedRfqsAction } from '@paradigm/features/src/unified-rfqs/ui/rfqs/pinned-rfqs-context';
import { useQuoteBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import Button from '@paradigm/design-system/src/components/Button';
import Text from '@paradigm/design-system/src/components/Text';

import WarningIcon from '@paradigm/design-system/src/assets/WarningIcon';
import { nepal, paradigmLightBlue } from '@paradigm/design-system/src/colors';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';

import { StrategyDescription } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { toast } from '#/components/UnifiedRfqs/toast';
import { useUnifiedRfqsService } from '@paradigm/features/src/unified-rfqs/contexts/unified-rfqs';
import { getProductName } from '@paradigm/features/src/products/domain';
import { OnQuoteMatchFound } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-matching/ob-quote-matching-provider';
import usePersistedMatchingDetection from '@paradigm/features/src/preferences/usePersistedMatchingDetection';
import { dismissToast } from '#/components/toasts/toast';
import { useTrackAction } from '@paradigm/features/src/analytics';

export default function useQuoteMatchFoundCallback() {
  const { pinRfq } = usePinnedRfqsAction();
  const { open: openQuoteBuilder } = useQuoteBuilderModalMethods();
  const track = useTrackAction();
  const { selectById } = useUnifiedRfqsService();
  const { isMatchingDetectionEnabled } = usePersistedMatchingDetection();
  const isMobile = useIsMobile();

  const onQuoteMatchFound = useCallback<OnQuoteMatchFound>(
    (tradeData, quote) => {
      if (!isMatchingDetectionEnabled) return;
      const rfq = selectById(tradeData.obId);
      const productName = getProductName(rfq?.product_codes[0]);
      const toastId = `ob-matching-quote-toast-${quote.id}`;
      pinRfq(tradeData.obId);
      track('received match quote', {
        side: tradeData.side,
        tradePrice: tradeData.price,
        quotePrice: quote.price,
      });
      toast(
        <div data-testid="toast-quote-match-notification">
          <Text
            as="div"
            color={nepal}
            weight="light"
            css={`
              white-space: ${isMobile ? 'pre-wrap' : 'nowrap'};
              font-size: 0.85rem;
            `}
          >
            More size posted on Order Book for{' '}
            <Text as="span" color={nepal} weight="medium">
              {productName}
            </Text>
          </Text>
          <StrategyDescription
            css="color: white; font-weight: bold; margin-bottom: 6px; font-size: 0.85rem;"
            whiteSpace={isMobile ? 'pre-wrap' : 'nowrap'}
          >
            {tradeData.description}
          </StrategyDescription>
          <Button
            variant="link"
            onClick={() => {
              dismissToast(toastId);
              track('open match quote order book');
              openQuoteBuilder({
                rfqId: tradeData.obId,
                side: tradeData.side,
                quantity: quote.remaining_quantity,
                price: quote.price,
                clickedQuote: quote,
              });
            }}
            ariaLabel="open orderbook trade match"
          >
            Open Order Book
          </Button>
        </div>,
        {
          icon: <WarningIcon width={22} color={paradigmLightBlue} />,
          styles: {
            borderColor: paradigmLightBlue,
            minWidth: undefined,
            maxWidth: undefined,
          },
          autoClose: 30_000,
          toastId,
        },
      );
    },
    [
      openQuoteBuilder,
      pinRfq,
      selectById,
      track,
      isMatchingDetectionEnabled,
      isMobile,
    ],
  );

  return onQuoteMatchFound;
}
